import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import Wizard from '../../../components/wizard/Wizard';
import { Save, X, MapPin, Upload, Image as ImageIcon, Car, Settings, DollarSign, MapPin as MapPinIcon, Wrench, FileText, Camera, FileCheck, Loader2, Trash2, Star } from 'lucide-react';
import vehicleLocationService from '../../../services/vehicleLocationService';
import vehicleCancellationPolicyService from '../../../services/vehicleCancellationPolicyService';
import vehicleMakeService from '../../../services/vehicleMakeService';
import vehicleModelService from '../../../services/vehicleModelService';
import vehicleFuelTypeService from '../../../services/vehicleFuelTypeService';
import vehicleOdometerService from '../../../services/vehicleOdometerService';
import vehicleFeatureService from '../../../services/vehicleFeatureService';
import vehicleRuleService from '../../../services/vehicleRuleService';
import vehicleReferenceService from '../../../services/vehicleReferenceService';
import vehicleTypeService from '../../../services/vehicleTypeService';
import { normalizeCategoryIdsFromVehicle } from '../../../utils/vehicleCategoryDisplay';
import vehicleService from '../../../services/vehicleService';
import { uploadVehicleImages, uploadVehicleDocuments } from '../../../services/uploadService';
import { ROUTES } from '../../../constants/routes.constants';
import { useAuth } from '../../../context/AuthContext';
import { sendAdminUpdateNotification } from '../../../services/adminVehicleNotification';

function getPolicyTierType(policy) {
  const name = (policy?.name || '').toLowerCase();
  const cancellationTime = (policy?.cancellationTime || '').toLowerCase();
  if (name.includes('free') || name.includes('gratuite') || cancellationTime.includes('free') || cancellationTime.includes('gratuite')) {
    return 'free';
  }
  if (name.includes('12h') || cancellationTime.includes('12h') || cancellationTime.includes('moins de 12h')) {
    return 'less-12h';
  }
  if (name.includes('24h') || cancellationTime.includes('24h') || cancellationTime.includes('24 heures')) {
    return '24h-12h';
  }
  if (name.includes('48h') || cancellationTime.includes('48h') || cancellationTime.includes('48 heures')) {
    return '48h-24h';
  }
  return 'days-48h';
}

function buildFlexiblePolicyEntry(policy, existingCp) {
  if (existingCp) {
    return { policy: policy._id, percentage: existingCp.percentage ?? '' };
  }
  return { policy: policy._id, percentage: '' };
}

/** Mappe les politiques du véhicule API → formulaire (aligné AddVehicle). */
function mapVehicleCancellationForForm(vehicle) {
  const raw = vehicle.cancellationPolicies || [];
  if (raw.length === 0) {
    return {
      cancellationPolicyType: 'non-remboursable',
      cancellationPolicies: [],
      freeCancellationDays: '',
    };
  }
  let freeCancellationDays = '';
  const cancellationPolicies = raw.map((cp) => {
    const policyObj = cp.policy;
    const policyId = policyObj?._id || policyObj;
    const pct = cp.percentage;
    const tier =
      policyObj && typeof policyObj === 'object' && policyObj.name !== undefined
        ? getPolicyTierType(policyObj)
        : 'days-48h';
    if (tier === 'free') {
      if (cp.freeCancellationDays != null && cp.freeCancellationDays !== '') {
        freeCancellationDays = String(cp.freeCancellationDays);
      }
      return {
        policy: policyId,
        percentage: pct !== undefined && pct !== null ? String(pct) : '0',
      };
    }
    return {
      policy: policyId,
      percentage: pct !== undefined && pct !== null ? String(pct) : '',
    };
  });
  return {
    cancellationPolicyType: 'flexible',
    cancellationPolicies,
    freeCancellationDays,
  };
}

/**
 * Edit Vehicle Page with Multi-Step Wizard
 * Admin and Vendor can edit existing vehicles through a guided step-by-step process
 */
const EditVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isVendor = user?.role === 'vendor';
  const isAdmin = user?.role === 'admin';
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(true);
  const [successToast, setSuccessToast] = useState(null);
  const [vehicleError, setVehicleError] = useState('');
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form data state
  const [formData, setFormData] = useState({
    type: 'car',
    categories: [],
    specs: {
      brand: '',
      model: '',
      year: '',
      fuel: '',
      transmission: 'MANUAL',
      odometer: '',
      seats: 5,
    },
    licencePlateNumber: {
      part1: '',
      part2: '',
      part3: '',
    },
    minRentalDays: 1,
    insuranceCoverage: '',
    ageRestriction: {
      enabled: false,
      minimumAge: 18,
    },
    smokingAllowed: false,
    internationalTravelAllowed: false,
    pricing: {
      basePrice: '',
      currency: 'MAD',
      deposit: {
        value: '',
        managedBy: 'AGENCY',
      },
      weeklyDiscount: {
        enabled: false,
        value: '',
        type: 'PERCENTAGE',
      },
      monthlyDiscount: {
        enabled: false,
        value: '',
        type: 'PERCENTAGE',
      },
      homeDeliveryPrice: {
        enabled: false,
      },
      // Livraison à domicile multi-emplacements (max 3)
      deliveryLocations: [],
    },
    vehicleLocation: '',
    location: {
      coordinates: ['', ''],
      address: '',
      city: '',
    },
    features: [],
    cancellationPolicies: [],
    cancellationPolicyType: 'flexible',
    freeCancellationDays: '',
    rules: [],
    images: [],
    registrationCardFront: '',
    registrationCardBack: '',
    ministryAuthorization: '',
    isActive: false,
    customModelName: '',
  });

  // Data states
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [odometers, setOdometers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableEmplacements, setAvailableEmplacements] = useState([]);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [vehicleRules, setVehicleRules] = useState([]);

  // Vehicle types / catégories dynamiques
  const defaultCategories = [
    { value: 'Économie', label: 'Économie' },
    { value: 'Compact', label: 'Compact' },
    { value: 'Berline', label: 'Berline' },
    { value: 'SUV', label: 'SUV' },
    { value: 'Luxe', label: 'Luxe' },
    { value: 'Sport', label: 'Sport' },
  ];
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  
  // Loading states
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingFuelTypes, setLoadingFuelTypes] = useState(true);
  const [loadingOdometers, setLoadingOdometers] = useState(true);
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [imageFiles, setImageFiles] = useState([]);
  /** Aligné sur formData.images ; à l’envoi, cette entrée devient images[0] → mainImage côté API */
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [deliveryLocationToAdd, setDeliveryLocationToAdd] = useState('');

  const reorderMainFirst = (arr, idx) => {
    if (!arr || arr.length === 0) return [];
    const i = Math.min(Math.max(0, idx), arr.length - 1);
    const out = [...arr];
    const [selected] = out.splice(i, 1);
    out.unshift(selected);
    return out;
  };

  // Wizard steps configuration
  const steps = [
    {
      id: 'basic-info',
      title: 'Informations de base',
      description: 'Type, catégorie, marque, modèle',
      icon: Car,
    },
    {
      id: 'vehicle-details',
      title: 'Détails du véhicule',
      description: 'Plaque, assurance, restrictions',
      icon: Settings,
    },
    {
      id: 'pricing',
      title: 'Tarification',
      description: 'Prix, réductions, caution',
      icon: DollarSign,
    },
    {
      id: 'location',
      title: 'Localisation',
      description: 'Région, adresse, coordonnées',
      icon: MapPinIcon,
    },
    {
      id: 'features',
      title: 'Équipements',
      description: 'Sélection des équipements',
      icon: Wrench,
    },
    {
      id: 'policies-rules',
      title: 'Politiques & Règles',
      description: 'Annulation et règles du véhicule',
      icon: FileText,
    },
    {
      id: 'photos',
      title: 'Photos',
      description: 'Images du véhicule',
      icon: Camera,
    },
    {
      id: 'documents',
      title: 'Documents',
      description: 'Documents administratifs',
      icon: FileCheck,
    },
  ];

  // Load all data
  useEffect(() => {
    const loadAllData = async () => {
      try {
        const service = isVendor ? vehicleReferenceService : {
          getAllMakes: vehicleMakeService.getAllMakes,
          getAllFuelTypes: vehicleFuelTypeService.getAllFuelTypes,
          getAllOdometers: vehicleOdometerService.getAllOdometers,
          getAllFeatures: vehicleFeatureService.getAllFeatures,
          getAllLocations: vehicleLocationService.getAllLocations,
          getAllCancellationPolicies: vehicleCancellationPolicyService.getAllPolicies,
          getAllRules: vehicleRuleService.getAllRules,
        };

        const [makesRes, fuelRes, odometerRes, featuresRes, locationsRes, policiesRes, rulesRes] = await Promise.all([
          service.getAllMakes({ page: 1, limit: 100, status: 'active' }),
          service.getAllFuelTypes({ page: 1, limit: 100, status: 'active' }),
          service.getAllOdometers({ page: 1, limit: 100, status: 'active' }),
          service.getAllFeatures({ page: 1, limit: 100, status: 'active' }),
          service.getAllLocations({ page: 1, limit: 100, status: 'active' }),
          service.getAllCancellationPolicies({ page: 1, limit: 100, status: 'active' }),
          service.getAllRules({ page: 1, limit: 100, status: 'active' }),
        ]);

        if (makesRes.success || makesRes.data) setMakes(makesRes.data || makesRes || []);
        if (fuelRes.success || fuelRes.data) setFuelTypes(fuelRes.data || fuelRes || []);
        if (odometerRes.success || odometerRes.data) setOdometers(odometerRes.data || odometerRes || []);
        if (featuresRes.success || featuresRes.data) setFeatures(featuresRes.data || featuresRes || []);
        if (locationsRes.success || locationsRes.data) {
          const locationsData = locationsRes.data || locationsRes || [];
          setLocations(locationsData);
          setAvailableEmplacements(locationsData);
        }
        if (policiesRes.success || policiesRes.data) {
          const policies = policiesRes.data || policiesRes || [];
          setCancellationPolicies(policies);
        }
        if (rulesRes.success || rulesRes.data) setVehicleRules(rulesRes.data || rulesRes || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingMakes(false);
        setLoadingFuelTypes(false);
        setLoadingOdometers(false);
        setLoadingFeatures(false);
        setLoadingLocations(false);
        setLoadingPolicies(false);
        setLoadingRules(false);
      }
    };

    loadAllData();
  }, [isVendor]);

  useEffect(() => {
    if (formData.cancellationPolicyType !== 'flexible' || cancellationPolicies.length === 0) return;
    setFormData((prev) => {
      const existing = prev.cancellationPolicies || [];
      const next = cancellationPolicies.map((policy) => {
        const found = existing.find((cp) => cp.policy === policy._id);
        return buildFlexiblePolicyEntry(policy, found);
      });
      const same =
        next.length === existing.length &&
        next.every((row, i) => {
          const prevRow = existing[i];
          return prevRow && prevRow.policy === row.policy && prevRow.percentage === row.percentage;
        });
      if (same) return prev;
      return { ...prev, cancellationPolicies: next };
    });
  }, [formData.cancellationPolicyType, cancellationPolicies]);

  // Quand la livraison n'est plus activée, on supprime les emplacements sélectionnés
  useEffect(() => {
    if (!formData.pricing.homeDeliveryPrice.enabled && formData.pricing.deliveryLocations.length > 0) {
      setFormData(prev => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          deliveryLocations: [],
        },
      }));
    }
  }, [formData.pricing.homeDeliveryPrice.enabled, formData.pricing.deliveryLocations.length]);

  // Load vehicle types for dynamic categories (admin and vendor)
  useEffect(() => {
    const loadVehicleTypes = async () => {
      try {
        setLoadingCategories(true);
        // Utiliser l'API publique pour les vendors, l'API admin pour les admins
        const response = isVendor
          ? await vehicleTypeService.getPublicTypes()
          : await vehicleTypeService.getAllTypes({
              page: 1,
              limit: 100,
              status: 'active',
            });

        const typesArray = response.data || response || [];
        if (Array.isArray(typesArray) && typesArray.length > 0) {
          setVehicleTypes(typesArray);
          setCategories(
            typesArray.map((type) => ({
              value: type._id || type.id,
              label: type.name,
            }))
          );
        }
      } catch (error) {
        console.error('Error loading vehicle types:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadVehicleTypes();
  }, [isVendor]);

  useEffect(() => {
    const len = formData.images.length;
    setMainImageIndex((prev) => {
      if (len === 0) return 0;
      return Math.min(prev, len - 1);
    });
  }, [formData.images.length]);

  useEffect(() => {
    if (!successToast) return undefined;
    const timer = setTimeout(() => setSuccessToast(null), 6000);
    return () => clearTimeout(timer);
  }, [successToast]);

  // Load vehicle data for editing
  useEffect(() => {
    const loadVehicleData = async () => {
      if (!id) {
        setVehicleError('ID du véhicule manquant');
        setIsLoadingVehicle(false);
        return;
      }

      try {
        setIsLoadingVehicle(true);
        setVehicleError('');
        const response = await vehicleService.getVehicleById(id);
        
        if (response.success && response.data) {
          const vehicle = response.data;
          
          // Parse licence plate number
          const licencePlateParts = vehicle.licencePlateNumber 
            ? vehicle.licencePlateNumber.split('-')
            : ['', '', ''];
          
          // Transform vehicle data to formData format (IDs uniquement pour les checkboxes)
          const normalizedCategories = normalizeCategoryIdsFromVehicle(vehicle);

          setFormData({
            // Type de véhicule fixé à "car" (champ décoratif, non éditable)
            type: 'car',
            categories: normalizedCategories,
            specs: {
              brand: vehicle.specs?.brand?._id || vehicle.specs?.brand || '',
              model: vehicle.specs?.model?._id || vehicle.specs?.model || '',
              year: vehicle.specs?.year?.toString() || '',
              fuel: vehicle.specs?.fuel?._id || vehicle.specs?.fuel || '',
              transmission: vehicle.specs?.transmission || 'MANUAL',
              odometer: vehicle.specs?.odometer?._id || vehicle.specs?.odometer || '',
              seats: vehicle.specs?.seats || 5,
            },
            customModelName: vehicle.customModelName || '',
            licencePlateNumber: {
              part1: licencePlateParts[0] || '',
              part2: licencePlateParts[1] || '',
              part3: licencePlateParts[2] || '',
            },
            minRentalDays: vehicle.minRentalDays || 1,
            insuranceCoverage: vehicle.insuranceCoverage || '',
            ageRestriction: {
              enabled: vehicle.ageRestriction?.enabled || false,
              minimumAge: vehicle.ageRestriction?.minimumAge || 18,
            },
            smokingAllowed: vehicle.smokingAllowed || false,
            internationalTravelAllowed: vehicle.internationalTravelAllowed || false,
            pricing: {
              basePrice: vehicle.pricing?.basePrice?.toString() || '',
              currency: vehicle.pricing?.currency || 'MAD',
              deposit: {
                value: vehicle.pricing?.deposit?.value?.toString() || '',
                managedBy: 'AGENCY',
              },
              weeklyDiscount: {
                enabled: !!vehicle.pricing?.weeklyDiscount,
                value: vehicle.pricing?.weeklyDiscount?.value?.toString() || '',
                type: vehicle.pricing?.weeklyDiscount?.type || 'PERCENTAGE',
              },
              monthlyDiscount: {
                enabled: !!vehicle.pricing?.monthlyDiscount,
                value: vehicle.pricing?.monthlyDiscount?.value?.toString() || '',
                type: vehicle.pricing?.monthlyDiscount?.type || 'PERCENTAGE',
              },
              homeDeliveryPrice: {
                enabled: Array.isArray(vehicle.pricing?.deliveryLocations) && vehicle.pricing?.deliveryLocations.length > 0,
              },
              deliveryLocations: (vehicle.pricing?.deliveryLocations || []).map((dl) => ({
                location: dl?.location?._id || dl?.location,
                price: dl?.price?.toString?.() || dl?.price || 0,
              })),
            },
            vehicleLocation: vehicle.vehicleLocation?._id || vehicle.vehicleLocation || '',
            location: {
              coordinates: vehicle.location?.coordinates 
                ? [vehicle.location.coordinates[0]?.toString() || '', vehicle.location.coordinates[1]?.toString() || '']
                : ['', ''],
              address: vehicle.location?.address || '',
              city: vehicle.location?.city || '',
            },
            features: vehicle.features?.map(f => f._id || f) || [],
            ...mapVehicleCancellationForForm(vehicle),
            rules: vehicle.rules?.map(r => r._id || r) || [],
            images: vehicle.images || [],
            registrationCardFront: vehicle.registrationCardFront || '',
            registrationCardBack: vehicle.registrationCardBack || '',
            ministryAuthorization: vehicle.ministryAuthorization || '',
            isActive: vehicle.isActive || false,
          });
          const imgs = vehicle.images || [];
          let mainIdx = 0;
          if (vehicle.mainImage && imgs.length > 0) {
            const mi = imgs.findIndex((u) => u === vehicle.mainImage);
            if (mi >= 0) mainIdx = mi;
          }
          setMainImageIndex(mainIdx);
        } else {
          setVehicleError('Véhicule introuvable');
        }
      } catch (error) {
        console.error('Error loading vehicle:', error);
        setVehicleError('Erreur lors du chargement du véhicule: ' + (error.message || 'Erreur inconnue'));
      } finally {
        setIsLoadingVehicle(false);
      }
    };

    loadVehicleData();
  }, [id]);

  // Load models when brand changes
  useEffect(() => {
    const loadModels = async () => {
      if (!formData.specs.brand) {
        setModels([]);
        setLoadingModels(false);
        return;
      }

      try {
        setLoadingModels(true);
        const service = isVendor ? vehicleReferenceService : vehicleModelService;
        const response = await service.getAllModels({
          page: 1,
          limit: 100,
          status: 'active',
          make: formData.specs.brand,
        });
        if (response.success || response.data) {
          setModels(response.data || response || []);
        } else {
          setModels([]);
        }
      } catch (error) {
        console.error('Error loading models:', error);
        setModels([]);
      } finally {
        setLoadingModels(false);
      }
    };

    loadModels();
  }, [formData.specs.brand, isVendor]);

  // Google Maps initialization (same as before)
  useEffect(() => {
    if (!showMap) return;
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      if (window.google && window.google.maps) {
        if (!mapLoaded) setMapLoaded(true);
        setTimeout(initializeMap, 100);
      } else {
        existingScript.addEventListener('load', () => {
          setMapLoaded(true);
          setTimeout(initializeMap, 100);
        });
      }
      return;
    }

    if (!mapLoaded) {
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
      if (!apiKey) {
        console.warn('Google Maps API key not found');
        return;
      }
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=fr`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setMapLoaded(true);
        setTimeout(initializeMap, 100);
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        setMapLoaded(false);
      };
      document.head.appendChild(script);
    } else if (mapLoaded && window.google && window.google.maps) {
      setTimeout(initializeMap, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;
    const lat = formData.location.coordinates[1] ? parseFloat(formData.location.coordinates[1]) : 33.5731;
    const lng = formData.location.coordinates[0] ? parseFloat(formData.location.coordinates[0]) : -7.5898;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    if (formData.location.coordinates[0] && formData.location.coordinates[1]) {
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        draggable: true,
        title: 'Emplacement du véhicule',
      });
      markerRef.current = marker;
      marker.addListener('dragend', (e) => {
        updateLocationFromCoordinates(e.latLng.lat(), e.latLng.lng());
      });
    }

    map.addListener('click', (e) => {
      updateLocationFromCoordinates(e.latLng.lat(), e.latLng.lng());
    });
  };

  const updateLocationFromCoordinates = async (lat, lng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;
        const cityComponent = results[0].address_components.find(
          component => component.types.includes('locality') || component.types.includes('administrative_area_level_2')
        );
        const city = cityComponent ? cityComponent.long_name : '';

        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            coordinates: [lng.toString(), lat.toString()],
            address: address,
            city: city,
          },
        }));

        if (markerRef.current) {
          markerRef.current.setPosition({ lat, lng });
        } else if (mapInstanceRef.current) {
          const marker = new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstanceRef.current,
            draggable: true,
            title: 'Emplacement du véhicule',
          });
          markerRef.current = marker;
          marker.addListener('dragend', (e) => {
            updateLocationFromCoordinates(e.latLng.lat(), e.latLng.lng());
          });
        }

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
        }
      }
    });
  };

  // Form handlers
  const handleCategoryToggle = (categoryValue) => {
    setFormData((prev) => {
      const exists = prev.categories.includes(categoryValue);
      const nextCategories = exists
        ? prev.categories.filter((item) => item !== categoryValue)
        : [...prev.categories, categoryValue];

      return {
        ...prev,
        categories: nextCategories,
      };
    });

    if (errors.categories) {
      setErrors((prev) => ({ ...prev, categories: undefined }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('specs.')) {
      const field = name.split('.')[1];
      setFormData((prev) => {
        const otherId = models.find((m) => (m.name || '').trim().toLowerCase() === 'autre')?._id;
        let nextCustom = prev.customModelName;
        if (field === 'brand') nextCustom = '';
        if (field === 'model') {
          nextCustom = otherId && value === otherId ? prev.customModelName : '';
        }
        return {
          ...prev,
          specs: {
            ...prev.specs,
            [field]: value,
            ...(field === 'brand' ? { model: '' } : {}),
          },
          customModelName: nextCustom,
        };
      });
    } else if (name.startsWith('pricing.')) {
      const field = name.split('.')[1];
      if (field === 'weeklyDiscount' || field === 'monthlyDiscount' || field === 'homeDeliveryPrice' || field === 'deposit') {
        const subField = name.split('.')[2];
        setFormData(prev => ({
          ...prev,
          pricing: {
            ...prev.pricing,
            [field]: {
              ...prev.pricing[field],
              [subField]: subField === 'enabled' ? checked : value,
            },
          },
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          pricing: {
            ...prev.pricing,
            [field]: value,
          },
        }));
      }
    } else if (name.startsWith('ageRestriction.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        ageRestriction: {
          ...prev.ageRestriction,
          [field]: type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('location.')) {
      const field = name.split('.')[1];
      if (field !== 'coordinates') {
        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            [field]: value,
          },
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    setFormData(prev => ({
      ...prev,
      vehicleLocation: locationId,
    }));

    if (locationId) {
      const selectedLocation = locations.find(loc => loc._id === locationId);
      if (selectedLocation) {
        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            coordinates: [selectedLocation.longitude.toString(), selectedLocation.latitude.toString()],
            city: selectedLocation.cityName,
          },
        }));
      }
    }
  };

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(id => id !== featureId)
        : [...prev.features, featureId],
    }));
  };

  const handleRuleToggle = (ruleId) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.includes(ruleId)
        ? prev.rules.filter(id => id !== ruleId)
        : [...prev.rules, ruleId],
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length === 0) {
      alert('Veuillez sélectionner des fichiers image valides.');
      e.target.value = '';
      return;
    }

    const imageUrls = validFiles.map(file => URL.createObjectURL(file));
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...imageUrls],
    }));
    setImageFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const imageUrl = formData.images[index];
    if (imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    setMainImageIndex((prevMain) => {
      const n = formData.images.length;
      if (n <= 1) return 0;
      if (index < prevMain) return prevMain - 1;
      if (index === prevMain) return 0;
      return prevMain;
    });
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setImageFiles((prevFiles) => {
      if (!formData.images[index]?.startsWith?.('blob:')) {
        return prevFiles;
      }
      const blobIndex = formData.images
        .slice(0, index)
        .filter((img) => typeof img === 'string' && img.startsWith('blob:')).length;
      return prevFiles.filter((_, i) => i !== blobIndex);
    });
  };

  const handleDocumentUpload = (fieldName, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Format de fichier non supporté. Veuillez sélectionner une image (JPG, PNG, GIF, WEBP) ou un PDF.');
      e.target.value = '';
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. La taille maximale est de 5MB.');
      e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      [fieldName]: previewUrl,
      [`${fieldName}File`]: file,
    }));
  };

  const handleRemoveDocument = (fieldName) => {
    const currentUrl = formData[fieldName];
    if (currentUrl && currentUrl.startsWith('blob:')) {
      URL.revokeObjectURL(currentUrl);
    }
    setFormData(prev => ({
      ...prev,
      [fieldName]: '',
      [`${fieldName}File`]: null,
    }));
  };

  // Livraison à domicile multi-emplacements (max 3)
  const handleAddDeliveryLocation = (locationId) => {
    if (!locationId) return;
    setFormData(prev => {
      const current = prev.pricing.deliveryLocations || [];
      if (current.length >= 3) return prev;
      if (current.some(loc => loc.location === locationId)) return prev;
      return {
        ...prev,
        pricing: {
          ...prev.pricing,
          deliveryLocations: [
            ...current,
            { location: locationId, price: 0 },
          ],
        },
      };
    });
    setDeliveryLocationToAdd('');
  };

  const handleUpdateDeliveryPrice = (locationId, newPrice) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        deliveryLocations: (prev.pricing.deliveryLocations || []).map((loc) =>
          loc.location === locationId ? { ...loc, price: newPrice } : loc
        ),
      },
    }));
  };

  const handleRemoveDeliveryLocation = (locationId) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        deliveryLocations: (prev.pricing.deliveryLocations || []).filter(loc => loc.location !== locationId),
      },
    }));
  };

  // Validation by step
  const validateStep = (stepIndex) => {
    const newErrors = {};
    
    switch (stepIndex) {
      case 0: // Basic Info
        if (!Array.isArray(formData.categories) || formData.categories.length === 0) {
          newErrors.categories = 'Sélectionnez au moins une catégorie';
        }
        if (!formData.specs.brand) newErrors['specs.brand'] = 'La marque est requise';
        if (!formData.specs.model) newErrors['specs.model'] = 'Le modèle est requis';
        {
          const otherId = models.find((m) => (m.name || '').trim().toLowerCase() === 'autre')?._id;
          if (otherId && formData.specs.model === otherId && !String(formData.customModelName || '').trim()) {
            newErrors.customModelName = 'Précisez le modèle (champ obligatoire pour « Autre »)';
          }
        }
        if (!formData.specs.fuel) newErrors['specs.fuel'] = 'Le type de carburant est requis';
        break;
      case 1: // Vehicle Details
        // Optional fields, no strict validation
        break;
      case 2: // Pricing
        if (!formData.pricing.basePrice || parseFloat(formData.pricing.basePrice) <= 0) {
          newErrors['pricing.basePrice'] = 'Le prix de base est requis et doit être supérieur à 0';
        }
        if (formData.pricing.homeDeliveryPrice.enabled) {
          const deliveryLocations = formData.pricing.deliveryLocations || [];
          if (deliveryLocations.length < 1) {
            newErrors['pricing.deliveryLocations'] = 'Ajoutez au moins un emplacement de livraison';
          } else {
            const invalid = deliveryLocations.some((loc) => {
              const priceNum = loc?.price === '' || loc?.price === undefined ? NaN : parseFloat(loc.price);
              return !loc?.location || loc.location === '' || isNaN(priceNum) || priceNum < 0;
            });
            if (invalid) {
              newErrors['pricing.deliveryLocations'] = 'Veuillez renseigner un prix valide (>= 0) pour chaque emplacement';
            }
          }
        }
        break;
      case 3: // Location
        if (!formData.vehicleLocation) {
          const [lng, lat] = formData.location.coordinates;
          if (!lng || !lat) {
            newErrors.location = 'Sélectionnez un emplacement ou fournissez des coordonnées';
          } else {
            const longitude = parseFloat(lng);
            const latitude = parseFloat(lat);
            if (isNaN(longitude) || longitude < -180 || longitude > 180) {
              newErrors['location.longitude'] = 'La longitude doit être entre -180 et 180';
            }
            if (isNaN(latitude) || latitude < -90 || latitude > 90) {
              newErrors['location.latitude'] = 'La latitude doit être entre -90 et 90';
            }
          }
        }
        break;
      case 4: // Features - Optional
        break;
      case 5: // Policies & Rules
        if (formData.cancellationPolicyType === 'flexible') {
          if (cancellationPolicies.length === 0) {
            newErrors.cancellationPoliciesRoot =
              'Aucune politique d’annulation n’est disponible. Contactez l’administration.';
          }
          const daysRaw = formData.freeCancellationDays;
          const daysOk =
            daysRaw !== '' &&
            daysRaw !== undefined &&
            !Number.isNaN(parseInt(daysRaw, 10)) &&
            parseInt(daysRaw, 10) >= 0;

          cancellationPolicies.forEach((policy) => {
            const cp = formData.cancellationPolicies.find((c) => c.policy === policy._id);
            const tierType = getPolicyTierType(policy);
            if (tierType === 'free') {
              if (!daysOk) {
                newErrors.freeCancellationDays =
                  'Le nombre de jours est requis et doit être un entier supérieur ou égal à 0';
              }
            } else {
              const percentage = cp?.percentage !== '' && cp?.percentage !== undefined ? parseFloat(cp.percentage) : NaN;
              if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
                newErrors[`cancellationPolicy.${policy._id}`] =
                  'Le pourcentage de frais de retenue est requis et doit être entre 0 et 100';
              }
            }
          });
        }
        break;
      case 6: // Photos - Optional
        break;
      case 7: // Documents
        if (!formData.registrationCardFront) newErrors.registrationCardFront = 'La carte grise (recto) est requise';
        if (!formData.registrationCardBack) newErrors.registrationCardBack = 'La carte grise (verso) est requise';
        if (!formData.ministryAuthorization) newErrors.ministryAuthorization = 'L\'autorisation du ministère est requise';
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const canGoNext = () => {
    // Basic validation for each step
    switch (currentStep) {
      case 0: {
        const otherId = models.find((m) => (m.name || '').trim().toLowerCase() === 'autre')?._id;
        const otherOk =
          !otherId ||
          formData.specs.model !== otherId ||
          String(formData.customModelName || '').trim().length > 0;
        return (
          Array.isArray(formData.categories) &&
          formData.categories.length > 0 &&
          formData.specs.brand &&
          formData.specs.model &&
          otherOk &&
          formData.specs.fuel
        );
      }
      case 2:
        if (!formData.pricing.basePrice || parseFloat(formData.pricing.basePrice) <= 0) return false;
        if (!formData.pricing.homeDeliveryPrice.enabled) return true;
        const deliveryLocations = formData.pricing.deliveryLocations || [];
        if (deliveryLocations.length < 1) return false;
        return deliveryLocations.every((loc) => {
          const priceNum = loc?.price === '' || loc?.price === undefined ? NaN : parseFloat(loc.price);
          return !!loc?.location && !isNaN(priceNum) && priceNum >= 0;
        });
      case 3:
        return formData.vehicleLocation || (formData.location.coordinates[0] && formData.location.coordinates[1]);
      case 5:
        if (formData.cancellationPolicyType === 'non-remboursable') {
          return true;
        }
        if (cancellationPolicies.length === 0) {
          return false;
        }
        const daysOkNext =
          formData.freeCancellationDays !== '' &&
          formData.freeCancellationDays !== undefined &&
          !Number.isNaN(parseInt(formData.freeCancellationDays, 10)) &&
          parseInt(formData.freeCancellationDays, 10) >= 0;
        return cancellationPolicies.every((policy) => {
          const cp = formData.cancellationPolicies.find((c) => c.policy === policy._id);
          const tierType = getPolicyTierType(policy);
          if (tierType === 'free') {
            return daysOkNext;
          }
          const percentage =
            cp?.percentage !== '' && cp?.percentage !== undefined ? parseFloat(cp.percentage) : NaN;
          return !Number.isNaN(percentage) && percentage >= 0 && percentage <= 100;
        });
      case 7:
        return formData.registrationCardFront && formData.registrationCardBack && formData.ministryAuthorization;
      default:
        return true;
    }
  };

  // Final submission — shouldNotify : notifier le vendeur (admin uniquement, côté API)
  const handleSubmit = async (shouldNotify = false) => {
    if (!validateStep(7)) {
      setCurrentStep(7);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Token d\'authentification manquant. Veuillez vous reconnecter.');
      }

      // Images : réordonner (principale en premier) puis fusionner URLs existantes + fichiers blob → compatible mainImage = images[0]
      let fileCursor = 0;
      const fileSlotPerImage = formData.images.map((img) => {
        if (typeof img === 'string' && img.startsWith('blob:')) {
          return imageFiles[fileCursor++] ?? null;
        }
        return null;
      });

      const orderedImages = reorderMainFirst([...formData.images], mainImageIndex);
      const orderedFileSlots = reorderMainFirst([...fileSlotPerImage], mainImageIndex);
      const filesToUpload = orderedFileSlots.filter((f) => f != null);

      let uploadedImageUrls = [];
      if (filesToUpload.length > 0) {
        try {
          const imagesResponse = await uploadVehicleImages(filesToUpload);
          if (imagesResponse.success && imagesResponse.data) {
            uploadedImageUrls = imagesResponse.data.map((file) => file.url);
          }
        } catch (uploadError) {
          throw new Error('Erreur lors de l\'upload des images: ' + uploadError.message);
        }
      }

      let urlIdx = 0;
      const finalImages = orderedImages
        .map((img) => {
          if (typeof img === 'string' && img.startsWith('blob:')) {
            return uploadedImageUrls[urlIdx++] ?? null;
          }
          if (typeof img === 'string' && img.trim() && !img.startsWith('blob:')) {
            return img.trim();
          }
          return null;
        })
        .filter((img) => img != null && String(img).trim() !== '');

      // Upload documents
      const documentsToUpload = {};
      if (formData.registrationCardFrontFile) documentsToUpload.registrationCardFront = formData.registrationCardFrontFile;
      if (formData.registrationCardBackFile) documentsToUpload.registrationCardBack = formData.registrationCardBackFile;
      if (formData.ministryAuthorizationFile) documentsToUpload.ministryAuthorization = formData.ministryAuthorizationFile;

      let uploadedDocuments = {};
      if (Object.keys(documentsToUpload).length > 0) {
        try {
          const documentsResponse = await uploadVehicleDocuments(documentsToUpload);
          if (documentsResponse.success && documentsResponse.data) {
            uploadedDocuments = documentsResponse.data;
          }
        } catch (uploadError) {
          throw new Error('Erreur lors de l\'upload des documents: ' + uploadError.message);
        }
      }

      const licencePlateNumber = [
        formData.licencePlateNumber.part1,
        formData.licencePlateNumber.part2,
        formData.licencePlateNumber.part3,
      ].filter(part => part.trim()).join('-').toUpperCase() || undefined;

      const vehicleData = {
        licencePlateNumber,
        // Type décoratif (fixé à "car" côté front)
        type: formData.type || undefined,
        vehicleType: formData.categories?.[0] || undefined,
        categories: formData.categories,
        category: formData.categories?.[0] || undefined,
        specs: {
          brand: formData.specs.brand,
          model: formData.specs.model,
          year: formData.specs.year ? parseInt(formData.specs.year) : undefined,
          fuel: formData.specs.fuel,
          transmission: formData.specs.transmission,
          odometer: formData.specs.odometer || undefined,
          seats: parseInt(formData.specs.seats) || 5,
        },
        pricing: {
          basePrice: parseFloat(formData.pricing.basePrice),
          currency: formData.pricing.currency,
          deposit: formData.pricing.deposit?.value ? {
            value: parseFloat(formData.pricing.deposit.value),
            managedBy: 'AGENCY',
          } : undefined,
          weeklyDiscount: formData.pricing.weeklyDiscount.enabled ? {
            value: parseFloat(formData.pricing.weeklyDiscount.value),
            type: formData.pricing.weeklyDiscount.type,
          } : undefined,
          monthlyDiscount: formData.pricing.monthlyDiscount.enabled ? {
            value: parseFloat(formData.pricing.monthlyDiscount.value),
            type: formData.pricing.monthlyDiscount.type,
          } : undefined,
          homeDeliveryPrice: formData.pricing.homeDeliveryPrice.enabled
            ? parseFloat((formData.pricing.deliveryLocations?.[0]?.price ?? 0).toString())
            : undefined,
          deliveryLocations: formData.pricing.homeDeliveryPrice.enabled
            ? (formData.pricing.deliveryLocations || []).map((loc) => ({
                location: loc.location,
                price: parseFloat(loc.price),
              }))
            : [],
        },
        vehicleLocation: formData.vehicleLocation || undefined,
        location: {
          type: 'Point',
          coordinates: formData.vehicleLocation 
            ? undefined
            : [parseFloat(formData.location.coordinates[0]), parseFloat(formData.location.coordinates[1])],
          address: formData.location.address?.trim() || undefined,
          city: formData.location.city?.trim() || undefined,
        },
        features: formData.features.length > 0 ? formData.features : undefined,
        rules: formData.rules.length > 0 ? formData.rules : undefined,
        minRentalDays: parseInt(formData.minRentalDays) || 1,
        insuranceCoverage: formData.insuranceCoverage || undefined,
        ageRestriction: formData.ageRestriction.enabled ? {
          enabled: true,
          minimumAge: parseInt(formData.ageRestriction.minimumAge),
        } : undefined,
        smokingAllowed: formData.smokingAllowed,
        internationalTravelAllowed: formData.internationalTravelAllowed,
        ...(models.find((m) => (m.name || '').trim().toLowerCase() === 'autre')?._id === formData.specs.model &&
        String(formData.customModelName || '').trim()
          ? { otherModelName: String(formData.customModelName).trim() }
          : {}),
        cancellationPolicies:
          formData.cancellationPolicyType === 'non-remboursable'
            ? []
            : cancellationPolicies.map((policy) => {
                const cp = formData.cancellationPolicies.find((c) => c.policy === policy._id);
                const tierType = getPolicyTierType(policy);
                if (tierType === 'free') {
                  return {
                    policy: policy._id,
                    percentage: 0,
                    freeCancellationDays: formData.freeCancellationDays
                      ? parseInt(formData.freeCancellationDays, 10)
                      : undefined,
                  };
                }
                return {
                  policy: policy._id,
                  percentage: parseFloat(cp?.percentage),
                };
              }),
        images: finalImages,
        registrationCardFront: uploadedDocuments.registrationCardFront?.url || formData.registrationCardFront || undefined,
        registrationCardBack: uploadedDocuments.registrationCardBack?.url || formData.registrationCardBack || undefined,
        ministryAuthorization: uploadedDocuments.ministryAuthorization?.url || formData.ministryAuthorization || undefined,
        isActive: formData.isActive,
        ...(isAdmin ? { notifyVendedor: !!shouldNotify } : {}),
      };

      const response = await fetch(`${ROUTES.API_BASE_URL}/vehicles/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vehicleData),
      });

      if (!response.ok) {
        let errorMessage = 'Échec de la mise à jour du véhicule';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      let result = {};
      try {
        result = await response.json();
      } catch (e) {
        /* body vide */
      }

      if (isAdmin) {
        if (shouldNotify) {
          await sendAdminUpdateNotification(id);
        }
        const baseOk = 'Véhicule mis à jour.';
        const detail = result.vendorNotificationMessage
          ? ` ${result.vendorNotificationMessage}`
          : shouldNotify && result.vendorNotified
            ? ' Le vendeur a été notifié.'
            : !shouldNotify
              ? ' Aucune notification au vendeur.'
              : '';
        setSuccessToast(`${baseOk}${detail}`.trim());
        setTimeout(() => {
          navigate(ROUTES.ADMIN_VEHICLES_LIST);
        }, 2200);
      } else {
        alert('Véhicule mis à jour avec succès!');
        navigate(isVendor ? ROUTES.VENDOR_VEHICLES_LIST : ROUTES.ADMIN_VEHICLES_LIST);
      }
      } catch (error) {
        console.error('Error updating vehicle:', error);
        setSubmitError(error.message || 'Échec de la mise à jour du véhicule. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                  Type de véhicule <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="type"
                  name="type"
                  value="car"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all bg-gray-100"
                  disabled={true}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                {loadingCategories ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                    <p className="text-sm text-gray-500">Chargement...</p>
                  </div>
                ) : (
                  <div
                    className={`rounded-xl border p-3 ${
                      errors.categories ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map((category) => (
                        <label
                          key={category.value}
                          className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={formData.categories.includes(category.value)}
                            onChange={() => handleCategoryToggle(category.value)}
                            disabled={isSubmitting}
                            className="h-4 w-4 accent-[#27489E]"
                          />
                          <span className="text-sm text-gray-700">{category.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {errors.categories && (
                  <p className="mt-1 text-xs text-red-500">{errors.categories}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="specs.brand" className="block text-sm font-semibold text-gray-700 mb-2">
                  Marque <span className="text-red-500">*</span>
                </label>
                {loadingMakes ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                    <p className="text-sm text-gray-500">Chargement...</p>
                  </div>
                ) : (
                  <select
                    id="specs.brand"
                    name="specs.brand"
                    value={formData.specs.brand}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                      errors['specs.brand'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  >
                    <option value="">Sélectionner une marque...</option>
                    {makes.map((make) => (
                      <option key={make._id} value={make._id}>
                        {make.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors['specs.brand'] && <p className="mt-1 text-sm text-red-500">{errors['specs.brand']}</p>}
              </div>
              <div>
                <label htmlFor="specs.model" className="block text-sm font-semibold text-gray-700 mb-2">
                  Modèle <span className="text-red-500">*</span>
                </label>
                {loadingModels ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                    <p className="text-sm text-gray-500">Chargement...</p>
                  </div>
                ) : (
                  <select
                    id="specs.model"
                    name="specs.model"
                    value={formData.specs.model}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                      errors['specs.model'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting || !formData.specs.brand}
                  >
                    <option value="">Sélectionner un modèle...</option>
                    {models.map((model) => (
                      <option key={model._id} value={model._id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors['specs.model'] && <p className="mt-1 text-sm text-red-500">{errors['specs.model']}</p>}
                {(() => {
                  const otherId = models.find((m) => (m.name || '').trim().toLowerCase() === 'autre')?._id;
                  const showOther = otherId && formData.specs.model === otherId;
                  if (!showOther) return null;
                  return (
                    <div className="mt-3">
                      <label htmlFor="customModelName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Préciser le modèle <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="customModelName"
                        name="customModelName"
                        type="text"
                        value={formData.customModelName}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                          errors.customModelName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex. série limitée, import US…"
                        disabled={isSubmitting}
                      />
                      {errors.customModelName && (
                        <p className="mt-1 text-sm text-red-500">{errors.customModelName}</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-6">
              <div>
                <label htmlFor="specs.year" className="block text-sm font-semibold text-gray-700 mb-2">
                  Année
                </label>
                <input
                  type="number"
                  id="specs.year"
                  name="specs.year"
                  value={formData.specs.year}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  placeholder="2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="specs.transmission" className="block text-sm font-semibold text-gray-700 mb-2">
                  Transmission <span className="text-red-500">*</span>
                </label>
                <select
                  id="specs.transmission"
                  name="specs.transmission"
                  value={formData.specs.transmission}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="MANUAL">Manuelle</option>
                  <option value="AUTOMATIC">Automatique</option>
                </select>
              </div>
              <div>
                <label htmlFor="specs.odometer" className="block text-sm font-semibold text-gray-700 mb-2">
                  Kilométrage
                </label>
                {loadingOdometers ? (
                  <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                    <p className="text-sm text-gray-500">Chargement...</p>
                  </div>
                ) : (
                  <select
                    id="specs.odometer"
                    name="specs.odometer"
                    value={formData.specs.odometer}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                    disabled={isSubmitting}
                  >
                    <option value="">Sélectionner...</option>
                    {odometers.map((odometer) => (
                      <option key={odometer._id} value={odometer._id}>
                        {odometer.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label htmlFor="specs.seats" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre de sièges
                </label>
                <input
                  type="number"
                  id="specs.seats"
                  name="specs.seats"
                  value={formData.specs.seats}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  min="1"
                  max="50"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div>
              <label htmlFor="specs.fuel" className="block text-sm font-semibold text-gray-700 mb-2">
                Type de carburant <span className="text-red-500">*</span>
              </label>
              {loadingFuelTypes ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                  <p className="text-sm text-gray-500">Chargement...</p>
                </div>
              ) : (
                <select
                  id="specs.fuel"
                  name="specs.fuel"
                  value={formData.specs.fuel}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                    errors['specs.fuel'] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Sélectionner...</option>
                  {fuelTypes.map((fuel) => (
                    <option key={fuel._id} value={fuel._id}>
                      {fuel.name}
                    </option>
                  ))}
                </select>
              )}
              {errors['specs.fuel'] && <p className="mt-1 text-sm text-red-500">{errors['specs.fuel']}</p>}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Numéro de plaque d'immatriculation
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={formData.licencePlateNumber.part1}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
                    setFormData(prev => ({
                      ...prev,
                      licencePlateNumber: { ...prev.licencePlateNumber, part1: value },
                    }));
                    if (value.length === 5 && e.target.nextElementSibling) {
                      e.target.nextElementSibling.focus();
                    }
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] text-center text-lg font-semibold tracking-widest bg-gray-50"
                  placeholder="---"
                  maxLength={5}
                  disabled={isSubmitting}
                />
                <input
                  type="text"
                  value={formData.licencePlateNumber.part2}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
                    setFormData(prev => ({
                      ...prev,
                      licencePlateNumber: { ...prev.licencePlateNumber, part2: value },
                    }));
                    if (value.length === 1 && e.target.nextElementSibling) {
                      e.target.nextElementSibling.focus();
                    }
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] text-center text-lg font-semibold tracking-widest bg-gray-50"
                  placeholder="---"
                  maxLength={1}
                  disabled={isSubmitting}
                />
                <input
                  type="text"
                  value={formData.licencePlateNumber.part3}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                    setFormData(prev => ({
                      ...prev,
                      licencePlateNumber: { ...prev.licencePlateNumber, part3: value },
                    }));
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] text-center text-lg font-semibold tracking-widest bg-gray-50"
                  placeholder="---"
                  maxLength={2}
                  disabled={isSubmitting}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">Format: 12345-A-45</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="minRentalDays" className="block text-sm font-semibold text-gray-700 mb-2">
                  Jour de location minimum
                </label>
                <input
                  type="number"
                  id="minRentalDays"
                  name="minRentalDays"
                  value={formData.minRentalDays}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  min="1"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="insuranceCoverage" className="block text-sm font-semibold text-gray-700 mb-2">
                  Couverture d'assurance
                </label>
                <select
                  id="insuranceCoverage"
                  name="insuranceCoverage"
                  value={formData.insuranceCoverage}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="">Sélectionner...</option>
                  <option value="BASIC">BASIC</option>
                  <option value="FULL">FULL</option>
                </select>
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ageRestriction.enabled"
                  name="ageRestriction.enabled"
                  checked={formData.ageRestriction.enabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="ageRestriction.enabled" className="text-sm font-semibold text-gray-700">
                  Restriction d'âge
                </label>
              </div>
              {formData.ageRestriction.enabled && (
                <div>
                  <label htmlFor="ageRestriction.minimumAge" className="block text-sm font-semibold text-gray-700 mb-2">
                    Âge minimum
                  </label>
                  <input
                    type="number"
                    id="ageRestriction.minimumAge"
                    name="ageRestriction.minimumAge"
                    value={formData.ageRestriction.minimumAge}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                      errors['ageRestriction.minimumAge'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    min="18"
                    max="100"
                    disabled={isSubmitting}
                  />
                  {errors['ageRestriction.minimumAge'] && <p className="mt-1 text-sm text-red-500">{errors['ageRestriction.minimumAge']}</p>}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smokingAllowed"
                  name="smokingAllowed"
                  checked={formData.smokingAllowed}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="smokingAllowed" className="text-sm font-semibold text-gray-700">
                  Fumer autorisé
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="internationalTravelAllowed"
                  name="internationalTravelAllowed"
                  checked={formData.internationalTravelAllowed}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="internationalTravelAllowed" className="text-sm font-semibold text-gray-700">
                  Voyage international autorisé
                </label>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6 items-start">
              <div className="flex flex-col">
                <label htmlFor="pricing.basePrice" className="block text-sm font-semibold text-gray-700 mb-2">
                  Prix par jour (MAD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="pricing.basePrice"
                  name="pricing.basePrice"
                  value={formData.pricing.basePrice}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                    errors['pricing.basePrice'] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="500"
                  min="0"
                  step="0.01"
                  disabled={isSubmitting}
                />
                {errors['pricing.basePrice'] && <p className="mt-1 text-sm text-red-500">{errors['pricing.basePrice']}</p>}
              </div>
              <div className="flex flex-col">
                <label htmlFor="pricing.currency" className="block text-sm font-semibold text-gray-700 mb-2">
                  Devise
                </label>
                <select
                  id="pricing.currency"
                  name="pricing.currency"
                  value={formData.pricing.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="MAD">MAD</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label htmlFor="pricing.deposit.value" className="block text-sm font-semibold text-gray-700 mb-2">
                  Caution (MAD)
                </label>
                <input
                  type="number"
                  id="pricing.deposit.value"
                  name="pricing.deposit.value"
                  value={formData.pricing.deposit.value}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  placeholder="1000"
                  min="0"
                  step="0.01"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pricing.weeklyDiscount.enabled"
                  name="pricing.weeklyDiscount.enabled"
                  checked={formData.pricing.weeklyDiscount.enabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="pricing.weeklyDiscount.enabled" className="text-sm font-semibold text-gray-700">
                  Réduction hebdomadaire
                </label>
              </div>
              {formData.pricing.weeklyDiscount.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valeur</label>
                    <input
                      type="number"
                      name="pricing.weeklyDiscount.value"
                      value={formData.pricing.weeklyDiscount.value}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                      placeholder="10"
                      min="0"
                      step="0.01"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      name="pricing.weeklyDiscount.type"
                      value={formData.pricing.weeklyDiscount.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                      disabled={isSubmitting}
                    >
                      <option value="FIXED">Fixe (MAD)</option>
                      <option value="PERCENTAGE">Pourcentage (%)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pricing.monthlyDiscount.enabled"
                  name="pricing.monthlyDiscount.enabled"
                  checked={formData.pricing.monthlyDiscount.enabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="pricing.monthlyDiscount.enabled" className="text-sm font-semibold text-gray-700">
                  Réduction mensuelle
                </label>
              </div>
              {formData.pricing.monthlyDiscount.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valeur</label>
                    <input
                      type="number"
                      name="pricing.monthlyDiscount.value"
                      value={formData.pricing.monthlyDiscount.value}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                      placeholder="15"
                      min="0"
                      step="0.01"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      name="pricing.monthlyDiscount.type"
                      value={formData.pricing.monthlyDiscount.type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                      disabled={isSubmitting}
                    >
                      <option value="FIXED">Fixe (MAD)</option>
                      <option value="PERCENTAGE">Pourcentage (%)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pricing.homeDeliveryPrice.enabled"
                  name="pricing.homeDeliveryPrice.enabled"
                  checked={formData.pricing.homeDeliveryPrice.enabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="pricing.homeDeliveryPrice.enabled" className="text-sm font-semibold text-gray-700">
                  Livraison à domicile disponible
                </label>
              </div>
              <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                💡 Vous pouvez définir jusqu'à 3 zones ou villes de livraison, chacune avec son propre tarif personnalisé.
              </p>
              {formData.pricing.homeDeliveryPrice.enabled && (
                <div className="space-y-4">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emplacement de livraison
                      </label>
                      <select
                        value={deliveryLocationToAdd}
                        onChange={(e) => setDeliveryLocationToAdd(e.target.value)}
                        className="w-full h-11 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                        disabled={isSubmitting || formData.pricing.deliveryLocations.length >= 3}
                      >
                        <option value="">Sélectionner...</option>
                        {availableEmplacements
                          .filter((loc) =>
                            !(formData.pricing.deliveryLocations || []).some(
                              (selected) => selected.location === loc._id || selected.location === loc.id
                            )
                          )
                          .map((loc) => (
                            <option key={loc._id || loc.id} value={loc._id || loc.id}>
                              {loc.cityName || loc.name || 'Emplacement'}
                              {loc.country ? `, ${loc.country}` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddDeliveryLocation(deliveryLocationToAdd)}
                      className="h-11 w-44 px-4 py-2 bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white rounded-xl hover:shadow-lg hover:scale-105 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        isSubmitting ||
                        !deliveryLocationToAdd ||
                        formData.pricing.deliveryLocations.length >= 3
                      }
                    >
                      Ajouter
                    </button>
                  </div>

                  {formData.pricing.deliveryLocations.length > 0 && (
                    <div className="space-y-3">
                      {formData.pricing.deliveryLocations.map((loc) => {
                        const emplacement =
                          availableEmplacements.find((e) => e._id === loc.location || e.id === loc.location) || null;

                        return (
                          <div
                            key={loc.location}
                            className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl p-3"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {emplacement?.cityName || emplacement?.name || 'Emplacement'}
                                {emplacement?.country ? `, ${emplacement.country}` : ''}
                              </p>
                            </div>
                            <div className="w-40 flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={loc.price}
                                placeholder="Prix"
                                onChange={(e) => handleUpdateDeliveryPrice(loc.location, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                                disabled={isSubmitting}
                              />
                              <span className="text-sm text-gray-500 whitespace-nowrap">MAD</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveDeliveryLocation(loc.location)}
                              className="p-2 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700 transition"
                              disabled={isSubmitting}
                              title="Supprimer"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {errors['pricing.deliveryLocations'] && (
                    <p className="text-sm text-red-500">{errors['pricing.deliveryLocations']}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="vehicleLocation" className="block text-sm font-semibold text-gray-700 mb-2">
                Région (Ville)
              </label>
              {loadingLocations ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50">
                  <p className="text-sm text-gray-500">Chargement...</p>
                </div>
              ) : (
                <select
                  id="vehicleLocation"
                  name="vehicleLocation"
                  value={formData.vehicleLocation}
                  onChange={handleLocationChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="">Sélectionner une ville...</option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.cityName}, {location.country}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Sélectionnez une ville pour remplir automatiquement les coordonnées
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Adresse complète
              </label>
              <div className="relative">
                <div className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-xl bg-white focus-within:ring-2 focus-within:ring-[#27489E] focus-within:border-[#27489E]">
                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    id="location.address"
                    name="location.address"
                    value={formData.location.address}
                    onChange={handleChange}
                    className="flex-1 outline-none bg-transparent text-gray-700 placeholder:text-gray-400"
                    placeholder="Saisissez une adresse ou cliquez sur la carte"
                    disabled={isSubmitting}
                    onFocus={() => setShowMap(true)}
                  />
                  {formData.location.address && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          location: { ...prev.location, address: '', coordinates: ['', ''], city: '' },
                        }));
                        if (markerRef.current) {
                          markerRef.current.setMap(null);
                          markerRef.current = null;
                        }
                      }}
                      className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Appuyez sur la carte pour changer l'emplacement
              </p>
              {showMap && (
                <div className="mt-4 border border-gray-300 rounded-xl overflow-hidden relative" style={{ height: '400px' }}>
                  <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                  {!mapLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                      <div className="text-center">
                        <p className="text-gray-500 mb-2">Chargement de la carte...</p>
                        {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                          <p className="text-xs text-red-500">
                            Clé API Google Maps non configurée
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {formData.vehicleLocation && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700">
                    Coordonnées automatiquement remplies depuis la ville sélectionnée
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Longitude: {formData.location.coordinates[0]}, Latitude: {formData.location.coordinates[1]}
                  </p>
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="location.longitude" className="block text-sm font-semibold text-gray-700 mb-2">
                    Longitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="location.longitude"
                    value={formData.location.coordinates[0]}
                    readOnly
                    step="any"
                    min="-180"
                    max="180"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 cursor-not-allowed"
                    placeholder="-7.5898"
                  />
                  {errors['location.longitude'] && <p className="mt-1 text-sm text-red-500">{errors['location.longitude']}</p>}
                </div>
                <div>
                  <label htmlFor="location.latitude" className="block text-sm font-semibold text-gray-700 mb-2">
                    Latitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="location.latitude"
                    value={formData.location.coordinates[1]}
                    readOnly
                    step="any"
                    min="-90"
                    max="90"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 cursor-not-allowed"
                    placeholder="33.5731"
                  />
                  {errors['location.latitude'] && <p className="mt-1 text-sm text-red-500">{errors['location.latitude']}</p>}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {loadingFeatures ? (
              <p className="text-sm text-gray-500">Chargement des équipements...</p>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div key={feature._id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-[#27489E] hover:bg-blue-50 transition cursor-pointer" onClick={() => handleFeatureToggle(feature._id)}>
                    <input
                      type="checkbox"
                      id={`feature-${feature._id}`}
                      checked={formData.features.includes(feature._id)}
                      onChange={() => handleFeatureToggle(feature._id)}
                      className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                      disabled={isSubmitting}
                    />
                    <label htmlFor={`feature-${feature._id}`} className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                      {feature.name}
                    </label>
                  </div>
                ))}
              </div>
            )}
            {features.length === 0 && !loadingFeatures && (
              <p className="text-sm text-gray-500 italic text-center py-8">Aucun équipement disponible</p>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Type de politique d'annulation</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-all" style={{ borderColor: formData.cancellationPolicyType === 'non-remboursable' ? '#27489E' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="cancellationPolicyType"
                    value="non-remboursable"
                    checked={formData.cancellationPolicyType === 'non-remboursable'}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        cancellationPolicyType: e.target.value,
                        cancellationPolicies: [],
                        freeCancellationDays: '',
                      }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        Object.keys(next).forEach((k) => {
                          if (
                            k.startsWith('cancellationPolicy.') ||
                            k === 'freeCancellationDays' ||
                            k === 'cancellationPoliciesRoot'
                          ) {
                            delete next[k];
                          }
                        });
                        return next;
                      });
                    }}
                    className="w-5 h-5 text-[#27489E] border-gray-300 focus:ring-[#27489E]"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900">Non-remboursable (100% de frais)</div>
                    <div className="text-sm text-gray-600 mt-1">Aucun remboursement en cas d'annulation</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-all" style={{ borderColor: formData.cancellationPolicyType === 'flexible' ? '#27489E' : '#e5e7eb' }}>
                  <input
                    type="radio"
                    name="cancellationPolicyType"
                    value="flexible"
                    checked={formData.cancellationPolicyType === 'flexible'}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        cancellationPolicyType: e.target.value,
                      }));
                    }}
                    className="w-5 h-5 text-[#27489E] border-gray-300 focus:ring-[#27489E]"
                    disabled={isSubmitting}
                  />
                  <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900">Politique flexible (Paliers personnalisés)</div>
                    <div className="text-sm text-gray-600 mt-1">Configurez des paliers de remboursement personnalisés</div>
                  </div>
                </label>
              </div>
            </div>

            {formData.cancellationPolicyType === 'flexible' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                  <span className="font-semibold text-[#27489E]">Politique flexible :</span> toutes les politiques
                  d’annulation définies pour la plateforme sont obligatoires. Renseignez chaque palier ci-dessous.
                </p>
                {errors.cancellationPoliciesRoot && (
                  <p className="text-sm text-red-600">{errors.cancellationPoliciesRoot}</p>
                )}
                {loadingPolicies ? (
                  <div className="p-6 border border-gray-200 rounded-xl bg-gray-50 text-center">
                    <p className="text-sm text-gray-500">Chargement des politiques...</p>
                  </div>
                ) : cancellationPolicies.length === 0 ? (
                  <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 text-center">
                    <p className="text-sm text-gray-500">Aucune politique d'annulation disponible</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cancellationPolicies.map((policy) => {
                      const cp = formData.cancellationPolicies.find(cp => cp.policy === policy._id);
                      const errorKey = `cancellationPolicy.${policy._id}`;
                      const tierType = getPolicyTierType(policy);
                      const isFreeCancel = tierType === 'free';
                      const pctVal = cp?.percentage;
                      const percentageInputValue =
                        pctVal === undefined || pctVal === null ? '' : String(pctVal);

                      return (
                        <div key={policy._id} className="bg-white rounded-xl shadow-sm border-2 border-[#27489E] p-6 transition-all">
                          <div className="mb-4">
                            <h4 className="text-base font-semibold text-gray-900">{policy.name}</h4>
                            <p className="text-sm text-gray-500 mt-1">{policy.cancellationTime}</p>
                            {policy.description && (
                              <p className="text-xs text-gray-600 mt-2">{policy.description}</p>
                            )}
                          </div>

                          <div className="mt-4 pt-4 border-t border-gray-200">
                              {isFreeCancel ? (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Jusqu'à X jours avant le départ <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={formData.freeCancellationDays}
                                      onChange={(e) => {
                                        setFormData(prev => ({ ...prev, freeCancellationDays: e.target.value }));
                                        if (errors.freeCancellationDays) {
                                          setErrors(prev => ({ ...prev, freeCancellationDays: undefined }));
                                        }
                                      }}
                                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                                        errors.freeCancellationDays ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                      placeholder="Ex: 7"
                                      disabled={isSubmitting}
                                    />
                                  </div>
                                  {errors.freeCancellationDays && (
                                    <p className="mt-1 text-sm text-red-500">{errors.freeCancellationDays}</p>
                                  )}
                                  <p className="mt-2 text-xs text-gray-600">
                                    Nombre de jours avant le départ pour une annulation gratuite. Le pourcentage de remboursement est fixé à 0% (100% de remboursement pour le client).
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Frais de retenue (%) <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={percentageInputValue}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const numValue = value ? parseFloat(value) : null;

                                        if (numValue !== null && (numValue > 100 || numValue < 0)) {
                                          return;
                                        }

                                        const newPolicies = [...formData.cancellationPolicies];
                                        const existingIndex = newPolicies.findIndex(p => p.policy === policy._id);

                                        if (existingIndex >= 0) {
                                          newPolicies[existingIndex].percentage = value;
                                        } else {
                                          newPolicies.push({ policy: policy._id, percentage: value });
                                        }
                                        setFormData(prev => ({ ...prev, cancellationPolicies: newPolicies }));

                                        if (errors[errorKey]) {
                                          setErrors(prev => ({ ...prev, [errorKey]: undefined }));
                                        }
                                      }}
                                      className={`w-full px-4 py-3 pr-10 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] transition-all ${
                                        errors[errorKey] ? 'border-red-500' : 'border-gray-300'
                                      }`}
                                      placeholder="Ex: 30"
                                      disabled={isSubmitting}
                                    />
                                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">%</span>
                                  </div>
                                  {errors[errorKey] && (
                                    <p className="mt-1 text-sm text-red-500">{errors[errorKey]}</p>
                                  )}
                                  <p className="mt-2 text-xs text-gray-600">
                                    Si vous réglez sur 30%, vous conserverez 30% du montant et le client sera remboursé de 70%.
                                  </p>
                                </div>
                              )}
                            </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Règles du véhicule</h3>
              {loadingRules ? (
                <p className="text-sm text-gray-500">Chargement des règles...</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {vehicleRules.map((rule) => (
                    <div key={rule._id} className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl hover:border-[#27489E] hover:bg-blue-50 transition">
                      <input
                        type="checkbox"
                        id={`rule-${rule._id}`}
                        checked={formData.rules.includes(rule._id)}
                        onChange={() => handleRuleToggle(rule._id)}
                        className="mt-1 w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                        disabled={isSubmitting}
                      />
                      <label htmlFor={`rule-${rule._id}`} className="flex-1 text-sm font-medium text-gray-700 cursor-pointer">
                        {rule.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {vehicleRules.length === 0 && !loadingRules && (
                <p className="text-sm text-gray-500 italic text-center py-8">Aucune règle disponible</p>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl p-4 mt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Options supplémentaires</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smokingAllowed"
                  name="smokingAllowed"
                  checked={formData.smokingAllowed}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#27489E] border-gray-300 rounded focus:ring-[#27489E]"
                  disabled={isSubmitting}
                />
                <label htmlFor="smokingAllowed" className="text-sm font-semibold text-gray-700">
                  Fumer autorisé
                </label>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={isSubmitting}
                />
                <div className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#27489E] hover:bg-gray-50 transition cursor-pointer">
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Télécharger des photos</span>
                </div>
              </label>
              <p className="mt-2 text-xs text-gray-500">
                Formats acceptés: JPG, PNG, GIF, WEBP. Vous pouvez sélectionner plusieurs images à la fois.
              </p>
            </div>
            {formData.images.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Cliquez sur une photo pour la définir comme <span className="font-semibold text-[#27489E]">photo principale</span> (affichée en premier et utilisée comme vignette).
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {formData.images.map((imageUrl, index) => {
                    const isMain = index === mainImageIndex;
                    return (
                      <div key={`${imageUrl}-${index}`} className="relative group">
                        <div
                          className={`aspect-square rounded-xl overflow-hidden bg-gray-100 ring-offset-2 transition-all ${
                            isMain
                              ? 'ring-4 ring-[#27489E] ring-offset-white shadow-md'
                              : 'border-2 border-gray-200'
                          }`}
                        >
                          <img
                            src={imageUrl}
                            alt={`Véhicule ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="14"%3EImage invalide%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                        {isMain && (
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md text-xs font-bold text-white bg-[#27489E] shadow">
                            Principale
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setMainImageIndex(index)}
                          className={`absolute top-2 left-2 p-1.5 rounded-full transition-colors ${
                            isMain
                              ? 'bg-amber-400 text-white shadow'
                              : 'bg-white/90 text-gray-500 opacity-0 group-hover:opacity-100 hover:bg-amber-100 hover:text-amber-600 border border-gray-200'
                          }`}
                          title="Définir comme photo principale"
                          disabled={isSubmitting}
                          aria-label="Définir comme photo principale"
                        >
                          <Star className={`w-4 h-4 ${isMain ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          disabled={isSubmitting}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {formData.images.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Aucune photo ajoutée</p>
                <p className="text-xs text-gray-400 mt-1">Ajoutez des photos pour mieux présenter votre véhicule</p>
              </div>
            )}
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Carte grise (Recto) <span className="text-red-500">*</span>
                </label>
                {errors.registrationCardFront && (
                  <p className="text-sm text-red-500">{errors.registrationCardFront}</p>
                )}
                {formData.registrationCardFront ? (
                  <div className="relative group">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100">
                      {formData.registrationCardFrontFile?.type === 'application/pdf' ? (
                        <div className="w-full h-full flex items-center justify-center bg-red-50">
                          <div className="text-center">
                            <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <p className="text-xs text-gray-600">PDF</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={formData.registrationCardFront}
                          alt="Carte grise recto"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument('registrationCardFront')}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleDocumentUpload('registrationCardFront', e)}
                      className="hidden"
                      disabled={isSubmitting}
                    />
                    <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#27489E] hover:bg-gray-50 transition">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-sm text-gray-600 text-center">Télécharger le recto</span>
                      <span className="text-xs text-gray-400">JPG, PNG, PDF (max 5MB)</span>
                    </div>
                  </label>
                )}
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Carte grise (Verso) <span className="text-red-500">*</span>
                </label>
                {errors.registrationCardBack && (
                  <p className="text-sm text-red-500">{errors.registrationCardBack}</p>
                )}
                {formData.registrationCardBack ? (
                  <div className="relative group">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100">
                      {formData.registrationCardBackFile?.type === 'application/pdf' ? (
                        <div className="w-full h-full flex items-center justify-center bg-red-50">
                          <div className="text-center">
                            <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <p className="text-xs text-gray-600">PDF</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={formData.registrationCardBack}
                          alt="Carte grise verso"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument('registrationCardBack')}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleDocumentUpload('registrationCardBack', e)}
                      className="hidden"
                      disabled={isSubmitting}
                    />
                    <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#27489E] hover:bg-gray-50 transition">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-sm text-gray-600 text-center">Télécharger le verso</span>
                      <span className="text-xs text-gray-400">JPG, PNG, PDF (max 5MB)</span>
                    </div>
                  </label>
                )}
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Autorisation du ministère <span className="text-red-500">*</span>
                </label>
                {errors.ministryAuthorization && (
                  <p className="text-sm text-red-500">{errors.ministryAuthorization}</p>
                )}
                {formData.ministryAuthorization ? (
                  <div className="relative group">
                    <div className="aspect-[4/3] rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100">
                      {formData.ministryAuthorizationFile?.type === 'application/pdf' ? (
                        <div className="w-full h-full flex items-center justify-center bg-blue-50">
                          <div className="text-center">
                            <svg className="w-12 h-12 text-blue-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <p className="text-xs text-gray-600">PDF</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={formData.ministryAuthorization}
                          alt="Autorisation ministère"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument('ministryAuthorization')}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      disabled={isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleDocumentUpload('ministryAuthorization', e)}
                      className="hidden"
                      disabled={isSubmitting}
                    />
                    <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#27489E] hover:bg-gray-50 transition">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-sm text-gray-600 text-center">Télécharger l'autorisation</span>
                      <span className="text-xs text-gray-400">JPG, PNG, PDF (max 5MB)</span>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoadingVehicle) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#27489E] mx-auto mb-4" />
            <p className="text-gray-600">Chargement du véhicule...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (vehicleError) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{vehicleError}</p>
            <button
              onClick={() => navigate(isVendor ? ROUTES.VENDOR_VEHICLES_LIST : ROUTES.ADMIN_VEHICLES_LIST)}
              className="px-4 py-2 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7d] transition"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {successToast && (
        <div
          className="fixed top-4 left-1/2 z-[100] -translate-x-1/2 px-5 py-3 rounded-xl shadow-lg bg-emerald-600 text-white text-sm font-medium max-w-md text-center"
          role="status"
        >
          {successToast}
        </div>
      )}
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Modifier un véhicule</h1>
          <p className="text-gray-500 mt-1">Modifiez les informations du véhicule étape par étape</p>
        </div>

        {submitError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <Wizard
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          steps={steps}
          onNext={handleNext}
          onPrevious={handlePrevious}
          canGoNext={canGoNext()}
          isSubmitting={isSubmitting}
        >
          {renderStepContent()}
        </Wizard>

        {/* Final Submit — admin : notifier ou non ; vendeur : un seul bouton */}
        {currentStep === steps.length - 1 && (
          <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition font-semibold flex items-center gap-2"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5" />
              Annuler
            </button>
            {isAdmin ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSubmit(false)}
                  className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isSubmitting || !canGoNext()}
                >
                  Mettre à jour sans notifier
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  className="px-6 py-3 bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white rounded-xl hover:shadow-lg hover:scale-105 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={isSubmitting || !canGoNext()}
                >
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Mise à jour...' : 'Mettre à jour et notifier le vendeur'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => handleSubmit(false)}
                className="px-6 py-3 bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white rounded-xl hover:shadow-lg hover:scale-105 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSubmitting || !canGoNext()}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Mise à jour...' : 'Mettre à jour le véhicule'}
              </button>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditVehicle;
